/**
 * LoaderTimeoutManager - Ensures global loader doesn't stay on indefinitely
 *
 * Purpose: If any async operation (iframe load, module import, etc.) fails to notify
 * the app that it's done, this manager forcefully hides the loader after a timeout.
 *
 * This is a SAFETY NET - it prevents the UI from appearing frozen when operations hang.
 * It does NOT affect normal operation when everything works correctly.
 */

export class LoaderTimeoutManager {
  private timeout: NodeJS.Timeout | null = null
  private readonly DEFAULT_TIMEOUT_MS = 20000 // 20 seconds - generous buffer
  private readonly onTimeoutCallback: ((reason: string) => void) | null = null

  constructor(onTimeoutCallback?: (reason: string) => void) {
    this.onTimeoutCallback = onTimeoutCallback || null
  }

  /**
   * Start a loader timeout
   * @param onTimeout Callback when timeout fires
   * @param timeoutMs Custom timeout duration (default: 20000ms)
   * @param reason Optional reason for logging
   */
  startLoaderTimeout(
    onTimeout: () => void,
    timeoutMs: number = this.DEFAULT_TIMEOUT_MS,
    reason: string = "No reason provided"
  ): void {
    // Clear any existing timeout first
    this.clearTimeout()

    console.log(
      `[LoaderTimeout] Starting loader timeout: ${timeoutMs}ms (${reason})`
    )

    this.timeout = setTimeout(() => {
      console.error(
        `[LoaderTimeout] Loader stuck for ${timeoutMs}ms, forcing hide (${reason})`
      )
      this.timeout = null

      // Call user's timeout handler
      onTimeout()

      // Also notify callback if provided
      if (this.onTimeoutCallback) {
        this.onTimeoutCallback(
          `Loader timeout after ${timeoutMs}ms: ${reason}`
        )
      }
    }, timeoutMs)
  }

  /**
   * Clear the timeout (call when operation completes successfully)
   */
  clearTimeout(): void {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
      console.log("[LoaderTimeout] Cleared loader timeout")
    }
  }

  /**
   * Reset the manager
   */
  reset(): void {
    this.clearTimeout()
  }

  /**
   * Check if timeout is currently active
   */
  isActive(): boolean {
    return this.timeout !== null
  }
}

// Export singleton instance for global use
export const loaderTimeoutManager = new LoaderTimeoutManager()
